import { Component, Show, createSignal } from "solid-js";
import { Button } from "../ui";

const MAX_LEN = 600;

export interface FocusConfirmBarProps {
  /** Detected focus text to prefill, or null when detection couldn't infer one. */
  candidate: string | null;
  rationale: string | null;
  busy: boolean;
  onConfirm: (focus: string) => void;
  onCancel: () => void;
}

export const FocusConfirmBar: Component<FocusConfirmBarProps> = (props) => {
  // Prefilled once from props.candidate — this is a confirmation step for a
  // single detection result, not a live reflection of a changing candidate.
  const [text, setText] = createSignal(props.candidate ?? "");

  const trimmed = () => text().trim();
  const isDisabled = () => trimmed().length === 0 || props.busy;

  const handleConfirm = () => {
    if (isDisabled()) return;
    props.onConfirm(trimmed());
  };

  return (
    <div class="sticky top-0 z-10 flex flex-col gap-2 px-6 py-3 bg-signal-magenta-tint border-b border-signal-magenta/25 text-ink">
      <div class="flex items-center gap-1.5 text-sm">
        <div role="status" class="flex items-center gap-1.5">
          <span class="font-mono text-[9px] uppercase tracking-[0.12em] px-1 rounded-sm border border-signal-magenta/25 text-signal-magenta">
            ai
          </span>{" "}
          <span class="font-medium">
            <Show
              when={props.candidate !== null}
              fallback="No focus could be inferred — describe what the team is focused on now"
            >
              Confirm the focus to prioritize for
            </Show>
          </span>
        </div>
      </div>
      <Show when={props.rationale}>
        {(rationale) => <p class="text-xs text-ink-2">{rationale()}</p>}
      </Show>
      <textarea
        maxLength={MAX_LEN}
        aria-label="Current focus"
        class="min-h-[60px] w-full resize-y rounded-md border border-line bg-surface p-2 text-sm text-ink focus-ring"
        value={text()}
        onInput={(e) => setText(e.currentTarget.value)}
        disabled={props.busy}
      />
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs text-ink-2">
          Used for this run only — not saved to the project.
        </span>
        <div class="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => props.onCancel()}
            disabled={props.busy}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={isDisabled()}
          >
            Use this focus
          </Button>
        </div>
      </div>
    </div>
  );
};
